import { Button } from "@/components/ui/button";
import { CardWithHeader } from "@/components/card-with-header";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useSelectedApplication } from "@/hooks/use-selected-application";
import { getDefaultMerchantRoute, hasRole } from "@/lib/auth";
import { logger } from "@/lib/logger";
import { runFailureReasonsReport } from "@/lib/payment-reports-api";
import { canSeeReporting, MERCHANT_REPORTING_FLAG } from "@/lib/reporting";
import { useIsFeatureFlagEnabled } from "@/lib/feature-flags";
import { useAnalytics } from "@/lib/analytics";
import { createFileRoute, redirect } from "@tanstack/react-router";
import { useMutation } from "@tanstack/react-query";
import { Download, FileText, Loader2 } from "lucide-react";
import { useMemo, useState } from "react";
import { toast } from "sonner";

export const Route = createFileRoute(
  "/(app)/_main-layout/_reporting/reporting",
)({
  component: () => <ReportingPage />,
  beforeLoad: ({ context }) => {
    if (!hasRole("GENERATE_REPORTS", context.authorities)) {
      throw redirect({
        to: getDefaultMerchantRoute(context.authorities),
        search: { applicationId: context.selectedApplicationId },
      });
    }
    return context;
  },
});

function toDateInputValue(date: Date) {
  return date.toISOString().slice(0, 10);
}

function defaultStartDate() {
  const date = new Date();
  date.setUTCDate(date.getUTCDate() - 7);
  return toDateInputValue(date);
}

function ReportingPage() {
  const context = Route.useRouteContext();
  const reportingFlagEnabled = useIsFeatureFlagEnabled(MERCHANT_REPORTING_FLAG);

  const hasAccess = canSeeReporting({
    authorities: context.authorities,
    flagEnabled: reportingFlagEnabled,
  });

  if (!hasAccess) {
    return null;
  }

  return (
    <CardWithHeader
      variant="exchange"
      label="Reporting"
      title="Reports"
      fullHeight={false}
    >
      <p className="mb-6 text-sm text-slate-500">
        Generate and download reports for the selected application.
      </p>
      <div className="space-y-3">
        <FailureReasonsReportCard />
      </div>
    </CardWithHeader>
  );
}

function FailureReasonsReportCard() {
  const context = Route.useRouteContext();
  const selectedApplication = useSelectedApplication();
  const analytics = useAnalytics();
  const [startDate, setStartDate] = useState(defaultStartDate);
  const [endDate, setEndDate] = useState(() => toDateInputValue(new Date()));

  const dateRangeInvalid = useMemo(
    () => startDate.length > 0 && endDate.length > 0 && startDate > endDate,
    [endDate, startDate],
  );

  const reportMutation = useMutation({
    mutationFn: () =>
      runFailureReasonsReport({
        accessToken: context.accessToken,
        applicationId: selectedApplication.applicationId,
        startDate,
        endDate,
      }),
    onSuccess: (report) => {
      const link = document.createElement("a");
      link.href = report.url;
      link.download = report.fileName;
      link.rel = "noreferrer";
      link.click();
      analytics.track("payment_report_downloaded", {
        report_type: "failure_reasons",
        application_id: selectedApplication.applicationId,
        merchant_id: context.merchantId,
        start_date: startDate,
        end_date: endDate,
        file_name: report.fileName,
        content_type: report.contentType,
        expires_in_seconds: report.expiresInSeconds,
      });
      toast.success("Report generated.");
    },
    onError: (error) => {
      logger.error("Failed to run failure reasons report", {}, error);
      toast.error("There was an error generating your report.");
    },
  });

  return (
    <section className="rounded-2xl border border-volume-text-purple/20 bg-white p-5">
      <div className="flex items-start gap-3">
        <div className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-volume-light-purple/40 text-volume-text-purple">
          <FileText className="size-5" />
        </div>
        <div className="min-w-0 flex-1">
          <h3 className="text-sm font-semibold text-slate-900">
            Failure reasons
          </h3>
          <p className="mt-0.5 text-sm text-slate-500">
            Failed payment rows for the selected UTC date range.
          </p>
        </div>
      </div>
      <form
        className="mt-4 grid gap-4 md:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto]"
        onSubmit={(event) => {
          event.preventDefault();
          if (dateRangeInvalid || reportMutation.isPending) return;
          reportMutation.mutate();
        }}
      >
        <div className="space-y-2">
          <Label htmlFor="failure-reasons-start-date">Start date</Label>
          <Input
            id="failure-reasons-start-date"
            type="date"
            value={startDate}
            onChange={(event) => setStartDate(event.target.value)}
            required
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="failure-reasons-end-date">End date</Label>
          <Input
            id="failure-reasons-end-date"
            type="date"
            value={endDate}
            onChange={(event) => setEndDate(event.target.value)}
            required
          />
        </div>
        <div className="flex items-end">
          <Button
            type="submit"
            className="h-9 min-w-28 gap-2"
            disabled={dateRangeInvalid || reportMutation.isPending}
          >
            {reportMutation.isPending ? (
              <Loader2 className="size-4 animate-spin" />
            ) : (
              <Download className="size-4" />
            )}
            Run
          </Button>
        </div>
      </form>
      {dateRangeInvalid ? (
        <p className="mt-3 text-sm text-red-600">
          Start date must be before or equal to end date.
        </p>
      ) : null}
    </section>
  );
}
